/**
 * Admin — Categorie di commessa (`project-categories`).
 * CRUD per-tenant con visibilità di gruppo holding (BE v1.24.0 `scope`).
 * Le categorie alimentano il select "Categoria" nella creazione commessa.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ActionIcon, Badge, Button, Card, Group, Modal, ScrollArea, SegmentedControl, SimpleGrid,
  Stack, Switch, Table, Text, Textarea, TextInput, Tooltip,
} from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { IconCategory, IconCircleCheck, IconPencil, IconPlus, IconTrash, IconUsersGroup } from '@tabler/icons-react';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { StatCard } from '@/shared/components/StatCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import {
  useCreateResource, useDeleteResource, useResourceList, useUpdateResource,
} from '@/shared/api/jsonapi/hooks';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { notifyError, notifySuccess } from '@/shared/lib/notify';
import {
  GROUP_SCOPE_OPTIONS, groupScopeColor, groupScopeLabel, isForeignGroupRecord, type GroupScope, GROUP_SCOPE_REQUISITI } from '@/shared/lib/groupScope';
import { useCanCreateGroupScope, useCurrentTenantId } from '@/shared/hooks/useCurrentTenant';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { Paginated } from '@/shared/components/DataTable/TablePagination';
import { useExternalFields } from '@/features/admin/externalSync';
import { useExternalRowLock } from '@/features/admin/externalSyncLock';
import { ExternalSyncNotice } from '@/features/admin/ExternalSyncNotice';
import { ExternalOriginBadge, HandCorrectedFields } from '@/features/admin/ProvenienzaEsternaBadge';

export const Route = createFileRoute('/_app/admin/project-categories')({ component: ProjectCategoriesPage });

interface ProjectCategoryRow extends Denormalized {
  type: 'project-categories';
  name?: string | null;
  description?: string | null;
  isActive?: boolean | null;
  scope?: string | null;
  createdAt?: string | null;
  tenant?: { id: string } | null;
}

interface FormValues {
  name: string;
  description: string;
  isActive: boolean;
  scope: GroupScope;
}

function toForm(row: ProjectCategoryRow | null): FormValues {
  return {
    name: row?.name ?? '',
    description: row?.description ?? '',
    isActive: row?.isActive ?? true,
    scope: (row?.scope as GroupScope) ?? 'tenant',
  };
}

function ProjectCategoriesPage() {
  const currentTenantId = useCurrentTenantId();
  const canGroup = useCanCreateGroupScope();
  const [localOnly, setLocalOnly] = useState(false);

  const { data, isLoading, isError, error, refetch } = useResourceList<ProjectCategoryRow>(
    'project-categories',
    { page: { size: 100 }, sort: ['name'], include: ['tenant'], params: localOnly ? { includeGroup: 'false' } : undefined },
  );
  const create = useCreateResource<ProjectCategoryRow>('project-categories');
  const update = useUpdateResource<ProjectCategoryRow>('project-categories');
  const remove = useDeleteResource('project-categories');

  const [opened, { open, close }] = useDisclosure(false);
  const [editingRow, setEditingRow] = useState<ProjectCategoryRow | null>(null);
  const form = useForm<FormValues>({
    mode: 'controlled',
    initialValues: toForm(null),
    validate: { name: (v) => (!v.trim() ? 'Il nome è obbligatorio' : null) },
  });

  /* Con il connettore MaeWay v8 nome e stato delle categorie arrivano dal v8 (`syncedFields`):
     descrizione e visibilità restano nostre anche sulle righe di là. */
  const external = useExternalFields('project-categories', editingRow);
  const lockReason = useExternalRowLock('project-categories');

  const rows = data?.data ?? [];
  const activeCount = rows.filter((r) => r.isActive).length;
  const groupCount = rows.filter((r) => r.scope === 'group').length;

  const openCreate = () => { setEditingRow(null); form.setValues(toForm(null)); form.resetDirty(); open(); };
  const openEdit = (row: ProjectCategoryRow) => { setEditingRow(row); form.setValues(toForm(row)); form.resetDirty(); open(); };

  const submit = form.onSubmit(async (v) => {
    const attributes: Record<string, unknown> = {
      name: v.name.trim(),
      description: v.description.trim() || null,
      isActive: v.isActive,
      scope: v.scope,
    };
    try {
      if (editingRow) {
        await update.mutateAsync({ id: editingRow.id, attributes });
        notifySuccess('Categoria aggiornata');
      } else {
        await create.mutateAsync({ attributes });
        notifySuccess('Categoria creata');
      }
      close();
    } catch (e) {
      notifyError(e);
      if (e instanceof JsonApiHttpError) {
        for (const [field, detail] of Object.entries(e.toFieldErrors())) form.setFieldError(field, detail);
      }
    }
  });

  const confirmDelete = (row: ProjectCategoryRow) => {
    modals.openConfirmModal({
      title: 'Elimina categoria',
      children: <Text size="sm">Eliminare la categoria <strong>{row.name}</strong>?</Text>,
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: async () => { try { await remove.mutateAsync(row.id); notifySuccess('Categoria eliminata'); } catch (e) { notifyError(e); } },
    });
  };

  const saving = create.isPending || update.isPending;

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconCategory size={22} />}
        title="Categorie progetti"
        sub="Categorie per i progetti, con condivisione opzionale nel gruppo holding. Alimentano il campo «Categoria» in creazione progetto."
        actions={<Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Nuova categoria</Button>}
      />

      <Stack>
        {isError && <QueryErrorAlert error={error} onRetry={() => void refetch()} />}
        <ExternalSyncNotice resourceType="project-categories" records={rows} />

        {isLoading ? (
          <InlineLoader label="Caricamento delle categorie progetto…" minHeight={120} />
        ) : (
          <SimpleGrid cols={{ base: 2, sm: 3 }}>
            <StatCard icon={IconCategory} color="brand" label="Categorie" value={rows.length} />
            <StatCard icon={IconCircleCheck} color="green" label="Attive" value={activeCount} />
            <StatCard icon={IconUsersGroup} color="teal" label="Di gruppo" value={groupCount} />
          </SimpleGrid>
        )}

        <Group justify="flex-end">
          <Switch label="Solo locali" checked={localOnly} onChange={(e) => setLocalOnly(e.currentTarget.checked)} />
        </Group>

        <Card withBorder padding={0} radius="lg" shadow="sm">
          {isLoading ? (
            <InlineLoader label="Caricamento delle categorie progetto…" minHeight={160} />
          ) : rows.length === 0 ? (
            <EmptyState icon={IconCategory} title="Nessuna categoria"
              description="Le categorie raggruppano i progetti nei filtri e nei report."
              actionLabel="Nuova categoria" onAction={openCreate} />
          ) : (
            <Paginated rows={rows} label="categorie">
            {(pageRows) => (
            <ScrollArea type="auto">
              <Table highlightOnHover striped="odd" stickyHeader miw={640} verticalSpacing="sm" horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Nome</Table.Th>
                    <Table.Th>Descrizione</Table.Th>
                    <Table.Th w={110}>Ambito</Table.Th>
                    <Table.Th w={90}>Stato</Table.Th>
                    <Table.Th w={90} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {pageRows.map((row) => {
                    const foreign = isForeignGroupRecord(row, currentTenantId);
                    const deleteLocked = lockReason(row, 'La categoria');
                    return (
                      <Table.Tr key={row.id}>
                        <Table.Td>
                          <Group gap={6} wrap="nowrap">
                            <Text size="sm" fw={600}>{row.name || '—'}</Text>
                            <ExternalOriginBadge row={row} />
                          </Group>
                        </Table.Td>
                        <Table.Td><Text size="sm" c="dimmed" lineClamp={1}>{row.description || '—'}</Text></Table.Td>
                        <Table.Td><Badge variant="light" color={groupScopeColor(row.scope)}>{groupScopeLabel(row.scope)}</Badge></Table.Td>
                        <Table.Td>{row.isActive ? <Badge variant="light" color="green">Attiva</Badge> : <Badge variant="light" color="gray">Off</Badge>}</Table.Td>
                        <Table.Td>
                          <Group gap={4} wrap="nowrap" justify="flex-end">
                            <Tooltip label={foreign ? 'Categoria di gruppo di un\'altra società (sola lettura)' : 'Modifica'}>
                              <ActionIcon variant="subtle" disabled={foreign} onClick={() => openEdit(row)} aria-label="Modifica"><IconPencil size={16} /></ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={foreign ? 'Sola lettura' : deleteLocked ?? 'Elimina'}
                              multiline={Boolean(deleteLocked) && !foreign}
                              w={deleteLocked && !foreign ? 280 : undefined}
                            >
                              <ActionIcon variant="subtle" color="red" disabled={foreign || Boolean(deleteLocked)} onClick={() => confirmDelete(row)} aria-label="Elimina"><IconTrash size={16} /></ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
            )}
            </Paginated>
          )}
        </Card>
      </Stack>

      <Modal opened={opened} onClose={close} title={editingRow ? 'Modifica categoria' : 'Nuova categoria'} centered>
        <form onSubmit={submit}>
          <Stack>
            <HandCorrectedFields row={editingRow} resourceType="project-categories" />
            <TextInput label="Nome" required {...form.getInputProps('name')} {...external('name')} />
            <Textarea label="Descrizione" autosize minRows={2} {...form.getInputProps('description')} />
            <div>
              <Text size="sm" fw={500} mb={4}>Visibilità</Text>
              <SegmentedControl
                fullWidth
                data={GROUP_SCOPE_OPTIONS.map((o) => ({ ...o, disabled: o.value === 'group' && !canGroup }))}
                value={form.values.scope}
                onChange={(v) => form.setFieldValue('scope', v as GroupScope)}
              />
              {!canGroup && (
                <Text size="xs" c="dimmed" mt={4}>{GROUP_SCOPE_REQUISITI}</Text>
              )}
            </div>
            <Switch label="Attiva" checked={form.values.isActive} onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)} {...external('isActive')} />
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" onClick={close} disabled={saving}>Annulla</Button>
              <Button type="submit" loading={saving}>{editingRow ? 'Salva' : 'Crea'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </MaeShell>
  );
}
